import { Link, useLocation } from 'react-router-dom'
import {
  BookOpen,
  ChevronDown,
  ExternalLink,
  GraduationCap,
  LogOut,
  Menu as MenuIcon,
  Terminal,
  User,
} from 'lucide-react'
import Avatar from '../components/Avatar'
import Menu, { MenuItem, MenuSeparator } from '../components/Menu'
import LanguageSwitch from '../components/LanguageSwitch'
import ThemeSwitch from '../components/ThemeSwitch'
import { useHint } from '../components/useHint'
import MenuSearch from './MenuSearch'
import { signOut } from '../lib/api'
import { ROLES, findMenuEntry } from './navigation'
import { shell } from './strings'
import './Topbar.css'

/**
 * The admin header, cloned from `kt_app_header` in _AdminsBaseLayout.cshtml:
 * the mobile sidebar toggle, the header menu, and the account dropdown.
 *
 * The legacy header ran four equally-weighted text links, which made none of
 * them read as the primary one. They are regrouped here by what they actually
 * do, without losing any of them: the user panel is the only link that LEAVES
 * the admin app, so it takes the accent pill; the user guide is a reference
 * tool, so it joins the icon cluster; profile and sign-out belong to the
 * account, so they live under the account menu (where sign-out was already
 * duplicated).
 *
 * The language and theme controls both sit with the tools rather than in the
 * account menu: each changes the whole document, not anything about the
 * account. Together they are the legacy header's اللغة and الوضع submenus,
 * rebuilt as two controls you can read without opening them.
 */
export default function Topbar({ session, sidebarOpen, onToggleSidebar, onShowConsoleLogs }) {
  const userGuide = session.settings.AdminPanelUserManualUrl
  const canSeeConsoleLogs = session.roles.includes(ROLES.AdvancedSettingsManagers)
  const { pathname } = useLocation()
  const entry = findMenuEntry(pathname)
  const SectionIcon = entry?.group.icon

  /* The bar's three unlabelled glyphs. They carried `title`, which left the panel with two
     kinds of tooltip — the app's on every row action, the browser's up here. */
  const burger = useHint(shell.toggleSidebar)
  const guide = useHint(shell.userGuide)
  const logs = useHint(shell.consoleLogs)

  return (
    <header className="topbar" data-testid="topbar">
      <div className="topbar__start">
        <button
          type="button"
          className="topbar__burger"
          onClick={onToggleSidebar}
          aria-expanded={sidebarOpen}
          aria-controls="app-sidebar"
          /* English, exactly as the legacy layout has it. */
          aria-label={shell.toggleSidebar}
          {...burger.hint}
        >
          <MenuIcon size={20} strokeWidth={1.75} />
        </button>
        {burger.bubble}

        {entry && (
          <div className="topbar__context">
            {SectionIcon && (
              <SectionIcon className="topbar__context-icon" size={16} strokeWidth={1.75} />
            )}
            <span className="topbar__context-text">
              <bdi>{entry.group.title}</bdi>
            </span>
          </div>
        )}

        <MenuSearch session={session} />
      </div>

      <div className="topbar__end">
        {/* The learner's panel now lives in this bundle at /portal, so the jump
            is a route rather than a hop back to the MVC app.

            A NEW TAB, which is what the arrow on the end has always promised, and it lands
            on the courses rather than the panel's front door: an admin opening the learner
            side is going to look at a course. The admin screen they were on is still there
            when they come back, which same-tab navigation cost them. */}
        <Link
          className="topbar__launcher"
          to="/portal/courses"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="topbar__launcher-mark" aria-hidden="true">
            <GraduationCap size={15} strokeWidth={2} />
          </span>
          <span className="topbar__launcher-label">
            <bdi>{shell.userPanel}</bdi>
          </span>
          <ExternalLink className="topbar__launcher-out rtl-flip" size={13} strokeWidth={2} />
        </Link>

        <span className="topbar__sep" aria-hidden="true" />

        <ThemeSwitch />

        <LanguageSwitch />

        {userGuide && (
          <>
            <a
              className="topbar__icon-btn"
              href={userGuide}
              target="_blank"
              rel="noreferrer"
              aria-label={shell.userGuide}
              {...guide.hint}
            >
              <BookOpen size={18} strokeWidth={1.75} />
            </a>
            {guide.bubble}
          </>
        )}

        {canSeeConsoleLogs && (
          <>
            <button
              type="button"
              className="topbar__icon-btn"
              onClick={onShowConsoleLogs}
              aria-label={shell.consoleLogs}
              {...logs.hint}
            >
              <Terminal size={18} strokeWidth={1.75} />
            </button>
            {logs.bubble}
          </>
        )}

        <span className="topbar__sep" aria-hidden="true" />

        <Menu
          width="266px"
          trigger={({ open, toggle, id }) => (
            <button
              type="button"
              className={`topbar__account-trigger${open ? ' is-open' : ''}`}
              onClick={toggle}
              aria-haspopup="true"
              aria-expanded={open}
              aria-controls={id}
            >
              <Avatar name={session.username} size="sm" solid />
              <span className="topbar__account-name">
                <bdi>{session.username}</bdi>
              </span>
              <ChevronDown className="topbar__account-caret" size={15} strokeWidth={2} />
            </button>
          )}
        >
          <div className="topbar__identity">
            <Avatar name={session.username} size="lg" solid />
            <div className="topbar__identity-text">
              <span className="topbar__identity-name">
                <bdi>{session.username}</bdi>
              </span>
              <span className="topbar__identity-mail" dir="ltr">
                {session.email}
              </span>
            </div>
          </div>

          <MenuSeparator />

          <MenuItem as={Link} to="/profile" icon={User}>
            {shell.profile}
          </MenuItem>
          <MenuItem
            as={Link}
            to="/portal/courses"
            target="_blank"
            rel="noopener noreferrer"
            icon={GraduationCap}
          >
            {shell.userPanel}
          </MenuItem>
          {userGuide && (
            <MenuItem as="a" href={userGuide} target="_blank" rel="noreferrer" icon={BookOpen}>
              {shell.userGuide}
            </MenuItem>
          )}

          <MenuSeparator />

          {/* Ends the SERVER session. Navigating to /login would only move the
              user; the auth cookie is HttpOnly, so only POST /Logout clears it. */}
          <MenuItem onClick={signOut} icon={LogOut} danger>
            {shell.logout}
          </MenuItem>
        </Menu>
      </div>
    </header>
  )
}
